<template>
  <div>
    <div class="input-group">
      <dx-dropdown
        class="input-group-addon date-period-dropdown"
        :items="filteredPeriods"
        label="text"
        @select="onPeriodSelect"
        direction="left"
        :max-height="periodsMaxHeight"
        :disabled="disabled"
      >
        <fa-icon :icon="['fal', 'chevron-down']" />
      </dx-dropdown>
      <dx-input-date
        id="start-date"
        class="form-input text-center"
        v-model="localStartDate"
        @change="onStartDateChange"
        @blur="onStartDateBlur"
        :disabled="disabled"
      />
      <dx-input-date
        id="end-date"
        class="form-input text-center"
        v-model="localEndDate"
        @change="onEndDateChange"
        @blur="onEndDateBlur"
        :disabled="disabled"
      />
    </div>
    <FormInputHint v-if="validator" :validator="validator" />
  </div>
</template>

<script>
import moment from 'moment';
import { parsePeriod } from '@/helpers/date';
import FormInputHint from '@/components/form/InputHint.vue';

function hasError(validator) {
  return validator && validator.$error;
}

export default {
  name: 'DatePeriod',
  components: {
    FormInputHint,
  },
  props: {
    startDate: {
      type: String,
      default: null,
    },
    endDate: {
      type: String,
      default: null,
    },
    periods: {
      type: Array,
      default: null,
    },
    maxMonths: {
      type: Number,
      default: 6,
    },
    periodsMaxHeight: {
      type: Number,
      default: 500,
    },
    startDateValidator: {
      type: Object,
      default: null,
    },
    endDateValidator: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    defaultPeriods() {
      return [
        { value: 'today', text: 'Hoje' },
        { value: 'yesterday', text: 'Ontem' },
        { value: 'd:-7', text: 'Últimos 7 dias' },
        { value: 'month', text: 'Este mês' },
        { value: 'd:-30', text: 'Último mês' },
        { value: 'd:-60', text: 'Últimos 60 dias' },
        { value: 'd:-90', text: 'Últimos 3 meses' },
        { value: 'd:-120', text: 'Últimos 4 meses' },
        { value: 'd:-180', text: 'Últimos 6 meses' },
      ];
    },
    filteredPeriods() {
      const periodsToUse = this.periods || this.defaultPeriods;
      return periodsToUse.filter((period) => {
        if (/^month:-?\d+$/.test(period.value)) {
          const months = Math.abs(Number(period.value.replace(/^month:/, '')));
          return months <= this.maxMonths;
        }
        if (/^d:-?\d+$/.test(period.value)) {
          const days = Math.abs(Number(period.value.replace(/^d:/, '')));
          const maxDays = this.maxMonths * 30;
          return days <= maxDays;
        }
        return true;
      });
    },
    validator() {
      if (hasError(this.startDateValidator)) {
        return this.startDateValidator;
      }
      if (hasError(this.endDateValidator)) {
        return this.endDateValidator;
      }
      return null;
    },
    localStartDate: {
      get() {
        return this.startDate;
      },
      set(value) {
        this.$emit('update:startDate', value);
      },
    },
    localEndDate: {
      get() {
        return this.endDate;
      },
      set(value) {
        this.$emit('update:endDate', value);
      },
    },
  },
  methods: {
    onPeriodSelect({ value }) {
      if (value === 'yesterday') {
        const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');
        this.localStartDate = yesterday;
        this.localEndDate = yesterday;
        return;
      }
      if (value === 'today') {
        const today = moment().format('YYYY-MM-DD');
        this.localStartDate = today;
        this.localEndDate = today;
        return;
      }
      if (/^d:-?\d+$/.test(value)) {
        const days = Number(value.replace(/^d:/, ''));
        const today = moment().format('YYYY-MM-DD');
        if (days > 0) {
          this.localStartDate = today;
          this.localEndDate = moment().add(days, 'days').format('YYYY-MM-DD');
        } else {
          this.localStartDate = moment().subtract(Math.abs(days), 'days').format('YYYY-MM-DD');
          this.localEndDate = today;
        }
        return;
      }
      if (/^month:-?\d+$/.test(value)) {
        const months = Number(value.replace(/^month:/, ''));
        if (months < 0) {
          const days = Math.abs(months) * 30;
          const end = moment().format('YYYY-MM-DD');
          const start = moment().subtract(days, 'days').format('YYYY-MM-DD');
          this.localStartDate = start;
          this.localEndDate = end;
        } else {
          const start = moment().add(months, 'months').startOf('month').format('YYYY-MM-DD');
          const end = moment().add(months, 'months').endOf('month').format('YYYY-MM-DD');
          this.localStartDate = start;
          this.localEndDate = end;
        }
        return;
      }
      const { start, end } = parsePeriod(value);
      this.localStartDate = start;
      this.localEndDate = end;
    },
    onStartDateChange(value) {
      this.$emit('change:startDate', value);
    },
    onStartDateBlur(value) {
      this.$emit('blur:startDate', value);
    },
    onEndDateChange(value) {
      this.$emit('change:endDate', value);
    },
    onEndDateBlur(value) {
      this.$emit('blur:endDate', value);
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";

.date-period-dropdown {
  .menu {
    padding: 6px !important;
    border: 1px solid #e7e9ed;
    box-shadow: 0 0.05rem 0.2rem rgba(69, 77, 93, 0.3);
  }

  .menu-item {
    padding: 2px 0px !important;
    margin-top: 0px !important;
    border-bottom: none !important;

    a {
      color: $body-font-color;
      margin: 0 0 !important;
    }
  }
}
</style>
